/**
 * Download API functions
 */
import { api } from './client'

export interface Download {
  platform: string
  architecture: string
  downloadUrl: string
  fileSizeBytes: number
  sha256: string
  fileName: string
}

export interface Release {
  version: string
  channel: string
  publishedAt: string
  releaseNotes: string | null
  isPreRelease: boolean
  downloads: Download[]
}

export interface LatestReleaseResponse {
  version: string
  updateAvailable: boolean
  releaseNotes: string | null
  downloadUrl: string | null
  sha256: string | null
  fileSizeBytes: number | null
}

/**
 * Get the latest release
 */
export async function getLatestRelease(channel?: string) {
  const params = channel ? `?channel=${encodeURIComponent(channel)}` : ''
  return api.get<Release>(`/api/downloads/latest${params}`)
}

/**
 * Get all releases
 */
export async function getAllReleases(channel?: string, limit: number = 10) {
  const params = new URLSearchParams()
  if (channel) params.append('channel', channel)
  if (limit !== 10) params.append('limit', limit.toString())
  const queryString = params.toString() ? `?${params.toString()}` : ''
  return api.get<Release[]>(`/api/downloads/releases${queryString}`)
}

/**
 * Get a specific release by version
 */
export async function getReleaseByVersion(version: string) {
  return api.get<Release>(`/api/downloads/releases/${encodeURIComponent(version)}`)
}

/**
 * Get download URL for a specific version and platform
 */
export async function getDownload(
  version: string,
  platform: string,
  arch?: string
) {
  const params = arch ? `?arch=${encodeURIComponent(arch)}` : ''
  return api.get<Download>(
    `/api/downloads/${encodeURIComponent(version)}/${encodeURIComponent(platform)}${params}`
  )
}

/**
 * Check for updates (used by desktop app auto-update)
 */
export async function checkForUpdate(
  currentVersion: string,
  platform?: string,
  arch?: string,
  channel?: string
) {
  const params = new URLSearchParams({ currentVersion })
  if (platform) params.append('platform', platform)
  if (arch) params.append('arch', arch)
  if (channel) params.append('channel', channel)
  return api.get<LatestReleaseResponse>(`/api/downloads/check-update?${params.toString()}`)
}

/**
 * Format file size in bytes to human-readable string
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB']
  const k = 1024
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${units[i]}`
}

/**
 * Get platform display name
 */
export function getPlatformDisplayName(platform: string): string {
  switch (platform.toLowerCase()) {
    case 'macos':
      return 'macOS'
    case 'windows':
      return 'Windows'
    case 'linux':
      return 'Linux'
    default:
      return platform
  }
}

/**
 * Get architecture display name
 */
export function getArchDisplayName(arch: string): string {
  switch (arch.toLowerCase()) {
    case 'x64':
      return 'Intel/AMD (x64)'
    case 'arm64':
      return 'Apple Silicon (ARM)'
    case 'universal':
      return 'Universal'
    default:
      return arch
  }
}

/**
 * Detect user's platform
 */
export function detectUserPlatform(): 'MacOS' | 'Windows' | 'Linux' {
  const platform = navigator.platform.toLowerCase()
  const userAgent = navigator.userAgent.toLowerCase()

  if (platform.includes('mac') || userAgent.includes('mac')) {
    return 'MacOS'
  }
  if (platform.includes('linux') || userAgent.includes('linux')) {
    return 'Linux'
  }
  return 'Windows'
}

/**
 * Detect user's architecture
 */
export function detectUserArch(): 'X64' | 'Arm64' {
  // Check for Apple Silicon
  const userAgent = navigator.userAgent.toLowerCase()
  if (userAgent.includes('mac') && userAgent.includes('arm')) {
    return 'Arm64'
  }
  // Default to x64
  return 'X64'
}
